import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import {
  Platform,
  type StyleProp,
  StyleSheet,
  type ViewStyle,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { format } from 'date-fns';
import {
  BodyText,
  DisplayText,
  TextLinkify,
  theme,
  useResponsive,
} from '@sg/garnish';

/**
 * Terms for the loyalty reward.
 */
export const LoyaltyRewardTerms = (props: LoyaltyRewardTermsProps) => {
  const { title, terms = '', expirationDate, style } = props;

  const { match } = useResponsive();

  const expiration = expirationDate
    ? format(new Date(expirationDate), 'MMM d')
    : null;

  return (
    <SafeAreaView
      edges={SAFE_AREA_EDGES}
      style={[match([styles.containerXS, styles.containerSM]), style]}
    >
      <DisplayText sizeMatch={['24']}>{title}</DisplayText>

      {expiration ? (
        <BodyText sizeMatch={['12']} style={styles.expirationLabel}>
          <FormattedMessage
            {...messages.expirationDate}
            values={{ expiration }}
          />
        </BodyText>
      ) : null}

      <BodyText sizeMatch={['16']}>
        {/* @ts-expect-error TextLinkify expects multiple children but terms is single string */}
        <TextLinkify>{terms}</TextLinkify>
      </BodyText>
    </SafeAreaView>
  );
};

// ─── Constants ──────────────────────────────────────────────────────────────

const SAFE_AREA_EDGES = Platform.select({
  android: ['bottom' as const],
  default: [],
});

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages({
  expirationDate: {
    defaultMessage: 'Use by {expiration}',
    description: 'Loyalty | Reward | Terms | Expiration date',
  },
});

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  containerXS: {
    paddingHorizontal: theme.spacing['4'],
    paddingBottom: theme.spacing['10'],
    gap: theme.spacing['2'],
  },
  containerSM: {
    paddingHorizontal: theme.spacing['6'],
    paddingBottom: theme.spacing['10'],
    gap: theme.spacing['2'],
  },
  expirationLabel: {
    color: theme.colors.CHARCOAL,
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────

type LoyaltyRewardTermsProps = {
  title: string;
  terms?: string | null;
  expirationDate?: string | null;
  style?: StyleProp<ViewStyle>;
};
